import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Chip from '@mui/material/Chip';
import SoftChip from '../../../components/dashboard/SoftChip';
import { customerService } from '../../../services/api';
import { retryOnServerError } from '../../../components/dashboard/queryOptions';
import { displayStatus } from './collaboratorFilters';

// `expired` is UI-only (see displayStatus).
export const STATUS_OPTIONS = ['pending', 'invited', 'expired', 'active', 'suspended', 'removed'];

const STATUS_COLOR = {
  pending: 'warning',
  invited: 'info',
  expired: 'error',
  active: 'success',
  suspended: 'default',
  removed: 'error',
};

export function CollaboratorStatusChip({ t, collaborator, sx }) {
  const status = displayStatus(collaborator);
  return (
    <SoftChip
      size="small"
      label={t(`collaborators.statuses.${status}`)}
      color={STATUS_COLOR[status] ?? 'default'}
      sx={sx}
    />
  );
}

// Must match the messages in customer_service (core/services/
// collaborator_send_invitation.py and CollaboratorWriteSerializer) — swapped
// for translated text instead of showing the backend's English.
const INVITE_EMAIL_CONFLICT = 'This email already has access to another company on Beworks.';
export const EMAIL_DUPLICATE = 'A collaborator with this email already exists.';

export const inviteErrorMessage = (t, error) =>
  error?.response?.data?.email?.[0] === INVITE_EMAIL_CONFLICT
    ? t('collaborators.invite.emailConflict')
    : t('collaborators.invite.error');

export const capabilities = (collaborator) => collaborator?.authorization?.capabilities ?? {};

// "Gerente" for all-stores access; the store role when there's only one, or
// "N perfis" when the stores have different roles.
export const roleSummary = (t, collaborator) => {
  if (collaborator.business_scope === 'all') return collaborator.global_role?.name ?? '';
  const names = [...new Set(collaborator.memberships.map((m) => m.role.name))];
  if (names.length <= 1) return names[0] ?? '';
  return t('collaborators.memberships.rolesCount', { count: names.length });
};

// All-stores access is a special condition, so the list shows it as a chip
// instead of plain text like the store names. Outlined, so it doesn't read as
// one of the (filled) status chips.
export function AllStoresChip({ t }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      color="primary"
      label={t('collaborators.scopes.all')}
    />
  );
}

export const accessSummary = (t, collaborator) => {
  if (collaborator.business_scope === 'all') return t('collaborators.scopes.all');
  const stores = collaborator.memberships.map((m) => m.business.name);
  if (stores.length === 1) return stores[0];
  return t('collaborators.memberships.storesCount', { count: stores.length });
};

// What the current user can do with the collection (per-record capabilities
// come in each item's `authorization`). Undefined while loading.
export function useCollaboratorsCapabilities() {
  const { data } = useQuery({
    queryKey: ['collaborators', 'authorization'],
    retry: retryOnServerError,
    queryFn: () => customerService.getCollaboratorsAuthorization().then((res) => res.data.capabilities),
  });
  return data;
}

// Options shared by the list filters and the form: the customer's stores
// (same query as the stores page) and its active roles.
export function useAccessOptions() {
  const storesQuery = useQuery({
    queryKey: ['businesses'],
    retry: retryOnServerError,
    queryFn: () => customerService.listBusinesses().then((res) => res.data),
  });
  const rolesQuery = useQuery({
    queryKey: ['roles'],
    queryFn: () => customerService.getRoles().then((res) => res.data),
  });

  const stores = (storesQuery.data ?? [])
    .filter((store) => !store.is_deleted)
    .map((store) => ({ uuid: store.uuid, name: store.display_name }));
  const roles = (rolesQuery.data ?? []).filter((role) => role.is_active);

  return { stores, roles, isLoading: storesQuery.isLoading || rolesQuery.isLoading };
}
